import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import { McpServerConfig } from "@/hooks/useMcpServers";
import useApi from "@/hooks/useApi";

interface Props {
  existing: McpServerConfig | null;
  onSaved: (config: McpServerConfig) => void;
  onClose: () => void;
}

export default function McpServerModal({ existing, onSaved, onClose }: Props) {
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [url, setUrl] = useState(existing?.url ?? "");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const api = useApi();

  const isEdit = existing != null;

  async function handleSave() {
    if (!name.trim()) { setError("Name is required."); return; }
    if (!url.trim()) { setError("URL is required."); return; }
    if (!isEdit && !apiKey.trim()) { setError("API Key is required."); return; }

    setSaving(true);
    setError(null);

    try {
      let resp: Response;
      const payload = {
        name: name.trim(),
        description: description.trim(),
        url: url.trim(),
        apiKey: apiKey || undefined,
        enabled
      };

      if (isEdit) {
        resp = await api.admin.put(`McpServers/${existing.id}`, payload);
      } else {
        resp = await api.admin.post("McpServers", payload);
      }

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Save failed. Please try again.");
        return;
      }

      const saved = await resp.json() as McpServerConfig;
      onSaved(saved);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={true}>
      <ModalHeader
        title={isEdit ? `Edit: ${existing.name}` : "Add MCP Server"}
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Name</span>
            <input
              className="input"
              placeholder="e.g. Velaro NetSuite MCP"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Description</span>
            <input
              className="input"
              placeholder="e.g. Look up orders, customers, and cases in NetSuite"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <span className="text-xs text-slate-400">
              Shown in the workflow node picker so agents know what this server does.
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Server URL</span>
            <input
              className="input font-mono text-sm"
              placeholder="https://mcp.example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">
              API Key{isEdit && (
                <span className="text-slate-400 font-normal ml-1">
                  (leave blank to keep existing: {existing.apiKeyMasked})
                </span>
              )}
            </span>
            <input
              className="input font-mono text-sm"
              type="password"
              placeholder={isEdit ? "Leave blank to keep existing key" : "Enter API key"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm">Enabled (available in workflow nodes)</span>
          </label>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2 py-2">
          <SecondaryBlueButton label="Cancel" onClick={onClose} />
          {saving ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              Saving... <LoadingSpinner />
            </div>
          ) : (
            <PrimaryBlueButton
              label={isEdit ? "Save Changes" : "Add Server"}
              onClick={handleSave}
            />
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
}
